import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import ClaimsTableRow from './ClaimsTableRow';

/**
 * Wraps the claims data table with loading/empty states and the header row.
 * Pure presentational; selection + handlers come from the parent.
 */
const ClaimsLoadingState = () => (
  <div className="flex items-center justify-center h-64">
    <div className="w-8 h-8 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
  </div>
);

const ClaimsEmptyState = ({ onCreate }) => (
  <div className="text-center py-12">
    <FileText size={48} className="mx-auto mb-4 text-slate-300" />
    <p className="text-slate-500">No claims found</p>
    <Button variant="link" className="text-indigo-600 mt-2" onClick={onCreate}>
      Create your first claim
    </Button>
  </div>
);

const ClaimsTable = ({
  loading, filteredClaims, selectableCount, selectedClaims,
  onSelectAll, onSelect, onCopy, onSubmit, onDelete,
  onOpenAdjudication, onOpenTriage, onOpenCorrect, onOpenVoid, onMarkFixed,
}) => {
  const navigate = useNavigate();
  return (
    <Card className="border-slate-200">
      <CardContent className="p-0">
        {loading && <ClaimsLoadingState />}
        {!loading && filteredClaims.length === 0 && (
          <ClaimsEmptyState onCreate={() => navigate('/claims/new')} />
        )}
        {!loading && filteredClaims.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedClaims.length === selectableCount && selectableCount > 0}
                      onCheckedChange={onSelectAll}
                      data-testid="select-all-claims"
                    />
                  </TableHead>
                  <TableHead>Claim #</TableHead>
                  <TableHead className="hidden md:table-cell">Patient</TableHead>
                  <TableHead className="hidden lg:table-cell">Service</TableHead>
                  <TableHead className="hidden sm:table-cell">Date</TableHead>
                  <TableHead className="hidden sm:table-cell">Units</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClaims.map((claim) => (
                  <ClaimsTableRow
                    key={claim.id}
                    claim={claim}
                    isSelected={selectedClaims.includes(claim.id)}
                    onSelect={onSelect}
                    onEdit={() => navigate(`/claims/edit/${claim.id}`)}
                    onCopy={() => onCopy(claim.id)}
                    onSubmit={() => onSubmit(claim.id)}
                    onDelete={() => onDelete(claim.id)}
                    onOpenAdjudication={() => onOpenAdjudication({ id: claim.id, claim_number: claim.claim_number })}
                    onOpenTriage={(action) => onOpenTriage(claim, action)}
                    onOpenCorrect={() => onOpenCorrect(claim)}
                    onOpenVoid={() => onOpenVoid(claim)}
                    onMarkFixed={() => onMarkFixed(claim)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ClaimsTable;
